import { DateHelper } from "@bryntum/schedulerpro";
import { getEligibleResourceForSpotIncentive } from "@/other/utils/incentiveUtils.jsx";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import type { EntityRecord } from "@/types/entity";
import type { ISwivuProject } from "@/types/SwivuProject";

export interface SpotIncentiveFileData {
  fileId?: number | string;
  documentId?: number;
  file?: File;
  fileName?: string;
}

export interface SpotIncentivePrefill {
  selectedTask: unknown;
  initialFileId?: number;
  initialFileData?: SpotIncentiveFileData;
  initialName?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  initialResourceIds?: number[];
}

/**
 * Prepares file data for the SpotIncentive editor.
 * Passes both the Swivu file id (for preview/save) and document id (for AI Adjustment).
 */
export async function prepareFileForSpotIncentive(
  file: SwivuFile
): Promise<SpotIncentiveFileData | null> {
  try {
    return {
      fileId: file.id,
      documentId: typeof file.document === "number" ? file.document : undefined,
      fileName: file.name
    };
  } catch (error) {
    console.error("Error preparing file for spot point:", error);
    return null;
  }
}

function resolveFileId(fileId: SpotIncentiveFileData["fileId"]): number | undefined {
  if (typeof fileId === "number" && Number.isFinite(fileId)) return fileId;
  if (typeof fileId === "string") {
    const parsed = Number(fileId);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** Name, dates, and eligible resources used to prefill Adjust Points from a file. */
export function buildSpotIncentivePrefill({
  file,
  fileData,
  task,
  project,
  entityRecord,
}: {
  file: SwivuFile;
  fileData: SpotIncentiveFileData | null;
  task: unknown;
  project: ISwivuProject;
  entityRecord: EntityRecord;
}): SpotIncentivePrefill {
  let initialName = "";
  let initialStartDate: string | undefined;
  let initialEndDate: string | undefined;
  let initialResourceIds: number[] = [];

  const taskId =
    task && typeof task === "object" && "id" in task ? Number((task as { id: number }).id) : undefined;
  const uploadedAt = file.createdAt;
  if (uploadedAt && taskId) {
    const uploadedDate = new Date(uploadedAt);
    uploadedDate.setHours(0, 0, 0, 0);
    initialStartDate = uploadedDate.toISOString();
    initialEndDate = uploadedDate.toISOString();
    try {
      const events = getEligibleResourceForSpotIncentive({
        taskId,
        project,
        startDate: uploadedDate,
        endDate: DateHelper.add(uploadedDate, 1, "day"),
      });
      initialResourceIds = events.map((event: { record: { resourceId: number } }) =>
        Number(event.record.resourceId),
      );
    } catch (error) {
      console.error("Error getting eligible resources:", error);
    }
  }

  if (file.id && entityRecord?.name) {
    initialName = `${entityRecord.name} - ${file.name}`;
  }

  const resolvedFileId = resolveFileId(fileData?.fileId);

  return {
    selectedTask: task,
    initialFileId: resolvedFileId,
    initialFileData: resolvedFileId
      ? { documentId: fileData?.documentId, fileName: fileData?.fileName }
      : fileData ?? undefined,
    initialName,
    initialStartDate,
    initialEndDate,
    initialResourceIds,
  };
}
